import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { 
  Shield, 
  ShieldCheck, 
  ShieldOff,
  Smartphone,
  Trash2,
  Loader2,
  Copy,
  Check,
  QrCode,
  Key,
  Monitor
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AccountTab = () => {
  const { user, setup2FA, enable2FA, disable2FA } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  // Fetch trusted devices
  const fetchTrustedDevices = async () => {
    if (!user?.totp_enabled) return;
    
    setLoadingDevices(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/2fa/trusted-devices`, {
        withCredentials: true
      });
      setTrustedDevices(response.data.devices || []);
    } catch (error) {
      console.error("Error fetching trusted devices:", error);
    } finally {
      setLoadingDevices(false);
    }
  };

  useEffect(() => {
    fetchTrustedDevices();
  }, [user?.totp_enabled]);

  // Start 2FA setup
  const handleStartSetup = async () => {
    setLoading(true);
    try {
      const data = await setup2FA();
      setSetupData(data);
      setVerificationCode("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start 2FA setup");
    } finally {
      setLoading(false);
    }
  };

  // Cancel setup
  const handleCancelSetup = () => {
    setSetupData(null);
    setVerificationCode("");
  };

  // Enable 2FA
  const handleEnable2FA = async () => {
    if (verificationCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setLoading(true);
    try {
      await enable2FA(verificationCode);
      toast.success("Two-factor authentication enabled!");
      setSetupData(null);
      setVerificationCode("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };

  // Disable 2FA
  const handleDisable2FA = async () => {
    if (disableCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setLoading(true);
    try {
      await disable2FA(disableCode);
      toast.success("Two-factor authentication disabled");
      setShowDisableDialog(false);
      setDisableCode("");
      setTrustedDevices([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };

  // Remove all trusted devices
  const handleRemoveTrustedDevices = async () => {
    setLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/auth/2fa/trusted-devices`, {
        withCredentials: true
      });
      toast.success("All trusted devices removed");
      setTrustedDevices([]);
    } catch (error) {
      toast.error("Failed to remove trusted devices");
    } finally {
      setLoading(false);
    }
  };

  // Copy secret to clipboard
  const handleCopySecret = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
      toast.success("Secret key copied to clipboard");
    }
  };

  // Generate QR code URL (using Google Charts API)
  const getQRCodeUrl = (uri) => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(uri)}`;
  };

  if (!user) return null;

  return (
    <div className="space-y-6" data-testid="account-tab">
      {/* User Profile Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Account Information
          </CardTitle>
          <CardDescription>Your account details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="w-16 h-16 rounded-full"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-2xl font-semibold text-primary">
                  {user.name?.charAt(0)?.toUpperCase() || "U"}
                </span>
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold">{user.name}</h3>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 2FA Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {user.totp_enabled ? (
              <ShieldCheck className="w-5 h-5 text-green-600" />
            ) : (
              <Shield className="w-5 h-5" />
            )}
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            {user.totp_enabled 
              ? "Your account is protected with 2FA" 
              : "Add an extra layer of security to your account"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* 2FA Disabled - Show enable option */}
          {!user.totp_enabled && !setupData && (
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
                <Smartphone className="w-8 h-8 text-muted-foreground mt-1" />
                <div>
                  <h4 className="font-medium">Authenticator App</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Use an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator 
                    to generate one-time codes when signing in.
                  </p>
                </div>
              </div>
              <Button onClick={handleStartSetup} disabled={loading} data-testid="enable-2fa-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                Enable Two-Factor Authentication
              </Button>
            </div>
          )}

          {/* 2FA Setup Flow */}
          {!user.totp_enabled && setupData && (
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row gap-6">
                {/* QR Code */}
                <div className="flex-shrink-0">
                  <div className="bg-white p-4 rounded-lg border shadow-sm inline-block">
                    <img 
                      src={getQRCodeUrl(setupData.uri)} 
                      alt="2FA QR Code" 
                      className="w-48 h-48"
                      data-testid="2fa-qr-code"
                    />
                  </div>
                </div>

                {/* Instructions */}
                <div className="flex-1 space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Setup Instructions</h4>
                    <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                      <li>Open your authenticator app</li>
                      <li>Scan the QR code or enter the secret key manually</li>
                      <li>Enter the 6-digit code from the app below</li>
                    </ol>
                  </div>

                  {/* Secret Key */}
                  <div>
                    <Label className="text-xs text-muted-foreground">Secret Key (for manual entry)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 bg-muted px-3 py-2 rounded text-sm font-mono break-all">
                        {setupData.secret}
                      </code>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={handleCopySecret}
                        data-testid="copy-secret-button"
                      >
                        {copiedSecret ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Verification Code Input */}
                  <div>
                    <Label htmlFor="verify-code">Verification Code</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        id="verify-code"
                        type="text"
                        placeholder="000000"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        className="text-center text-lg tracking-widest max-w-[150px]"
                        maxLength={6}
                        data-testid="setup-verification-code"
                      />
                      <Button 
                        onClick={handleEnable2FA} 
                        disabled={loading || verificationCode.length !== 6}
                        data-testid="verify-setup-button"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Verify & Enable
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button variant="outline" onClick={handleCancelSetup}>
                  Cancel Setup
                </Button>
              </div>
            </div>
          )}

          {/* 2FA Enabled - Show status and disable option */}
          {user.totp_enabled && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <ShieldCheck className="w-6 h-6 text-green-600" />
                <div>
                  <h4 className="font-medium text-green-800">2FA is Active</h4>
                  <p className="text-sm text-green-700">
                    Your account is protected with two-factor authentication
                  </p>
                </div>
              </div>

              {/* Trusted Devices */}
              {trustedDevices.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2">
                      <Monitor className="w-4 h-4" />
                      Trusted Devices ({trustedDevices.length})
                    </h4>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleRemoveTrustedDevices}
                      disabled={loading}
                      data-testid="remove-devices-button"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove All
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    These devices won't be asked for a 2FA code when signing in.
                  </p>
                </div>
              )}

              <Button 
                variant="destructive" 
                onClick={() => setShowDisableDialog(true)}
                data-testid="disable-2fa-button"
              >
                <ShieldOff className="w-4 h-4 mr-2" />
                Disable Two-Factor Authentication
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disable 2FA Dialog */}
      <AlertDialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Two-Factor Authentication?</AlertDialogTitle>
            <AlertDialogDescription>
              This will make your account less secure. Enter your current 2FA code to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="disable-code">Verification Code</Label>
            <Input
              id="disable-code"
              type="text"
              placeholder="000000"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="text-center text-lg tracking-widest max-w-[150px] mt-2"
              maxLength={6}
              data-testid="disable-verification-code"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDisableCode("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDisable2FA}
              disabled={loading || disableCode.length !== 6}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-disable-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Disable 2FA
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AccountTab;
